<script setup lang="ts">
import { ref } from "vue";
import { storeToRefs } from "pinia";

import { useAccount } from "@/stores/account.ts";
import { useEditTransaction } from "@/components/EditTransaction/state.ts";

import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/vue";
import { CheckIcon, ChevronUpDownIcon, PlusIcon } from "@heroicons/vue/24/outline";
import CreateLedger from "@/components/EditTransaction/CreateLedger.vue";
import type { Ledger } from "@/types/account";

const account = useAccount();
const state = useEditTransaction();

const { ledgers } = storeToRefs(account);
const { ledger } = storeToRefs(state);

const showCreateLedger = ref(false);

const getColorClass = (ledger: Ledger) => {
  return `background: ${ledger!.colour!}`;
};

const onCreateLedger = (): void => {
  showCreateLedger.value = true;
};
</script>

<template>
  <Listbox v-model="ledger" as="div" class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sp:py-6">
    <ListboxLabel class="block text-sm/6 font-medium text-gray-900">Account</ListboxLabel>
    <div class="relative w-full">
      <ListboxButton
        class="grid w-full cursor-default grid-cols-1 rounded-md bg-white py-1.5 pl-3 pr-2 text-left text-gray-900 outline -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
      >
        <span class="col-start-1 row-start-1 flex items-center gap-3 pr-6">
          <span
            v-if="ledger?.colour"
            class="inline-block size-2 shrink-0 rounded-full border border-transparent"
            :style="getColorClass(ledger)"
          />
          <span class="block truncate">{{ ledger?.name }}</span>
        </span>
        <ChevronUpDownIcon
          aria-hidden="true"
          class="col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4"
        />
      </ListboxButton>

      <transition
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <ListboxOptions
          class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
        >
          <ListboxOption
            v-for="l in ledgers"
            :key="l.id"
            v-slot="{ active }"
            :value="l"
            as="template"
          >
            <li
              :class="[
                active ? 'bg-indigo-600 text-white outline-none' : 'text-gray-900',
                'relative cursor-default select-none py-2 pl-3 pr-9',
              ]"
            >
              <div class="flex items-center">
                <span
                  v-if="l.colour"
                  class="inline-block size-2 shrink-0 rounded-full border border-transparent"
                  :style="getColorClass(l)"
                  aria-hidden="true"
                />
                <span
                  :class="[
                    l.id === ledger?.id ? 'font-semibold' : 'font-normal',
                    'ml-3 block truncate',
                  ]"
                >
                  {{ l.name }}
                </span>
              </div>

              <span
                v-if="l.id === ledger?.id"
                :class="[
                  active ? 'text-white' : 'text-indigo-600',
                  'absolute inset-y-0 right-0 flex items-center pr-4',
                ]"
              >
                <CheckIcon aria-hidden="true" class="size-5" />
              </span>
            </li>
          </ListboxOption>
        </ListboxOptions>
      </transition>
    </div>

    <button
      class="flex flex-row items-center gap-x-1.5 cursor-pointer text-sm"
      type="button"
      @click="onCreateLedger"
    >
      <PlusIcon class="size-4"></PlusIcon>
      Nieuw account
    </button>
  </Listbox>

  <CreateLedger v-if="showCreateLedger" @close="showCreateLedger = false"></CreateLedger>
</template>
